import { useEffect, useState } from 'react';

const QUERY = '(pointer: coarse)';

/**
 * Synchronous, non-hook check for a coarse (touch) primary pointer. Use this in
 * plain utilities and Fabric factories that cannot call the hook — e.g. to size
 * touch-friendly vertex handles at creation time (#747). Evaluated once when
 * called; it does not react to later pointer changes.
 *
 * @returns {boolean}
 */
export function isCoarsePointer() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(QUERY).matches;
}

/**
 * Returns true when the device's primary pointer is coarse (touch).
 * Reacts to pointer changes (e.g. attaching/detaching a mouse on hybrid
 * devices like Surface or iPad with Magic Keyboard trackpad).
 *
 * Use this for UI affordances that only make sense on touch input
 * (e.g. an explicit pan button) — independent of viewport size, so an
 * iPad in landscape (≥1024px) still counts as touch.
 */
export function useHasCoarsePointer() {
  const [coarse, setCoarse] = useState(isCoarsePointer);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(QUERY);
    const handler = (e) => setCoarse(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return coarse;
}
